import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  useUpdateStandingOpeningHours,
  useUpsertOpeningHoursException,
} from '@/features/location-opening-hours';
import { OpeningHoursImpactDescription } from '@/features/location-opening-hours/components/opening-hours-impact-description';
import { getOpeningHoursConfirmation } from '@/features/location-opening-hours/lib/confirmation';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import type { LocationOpeningHours } from '@borradh-workspace/api-client/types';

type EditScope = 'date' | 'standing';

interface EditOpeningHoursDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
  /** The date being clicked on the calendar. */
  date: Date;
  /** Effective hours for this day before edit, used to seed the form. */
  initial: { fromMinutes: number; toMinutes: number; closed: boolean };
  /** Standing weekly schedule, used when applying scope='standing'. */
  standing: LocationOpeningHours | null;
  /** Org default schedule, fallback when standing is null. */
  organizationDefault: LocationOpeningHours | null;
}

const minutesToHHMM = (m: number) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};
const hhmmToMinutes = (s: string) => {
  const [h, m] = s.split(':').map(Number);
  return h * 60 + m;
};

export function EditOpeningHoursDialog({
  open,
  onOpenChange,
  locationId,
  date,
  initial,
  standing,
  organizationDefault,
}: EditOpeningHoursDialogProps) {
  const [scope, setScope] = useState<EditScope>('date');
  const [closed, setClosed] = useState(initial.closed);
  const [from, setFrom] = useState(minutesToHHMM(initial.fromMinutes));
  const [to, setTo] = useState(minutesToHHMM(initial.toMinutes));
  const [pendingImpact, setPendingImpact] =
    useState<ReturnType<typeof getOpeningHoursConfirmation>>(null);

  // Reset form when the dialog re-opens for a different day.
  useEffect(() => {
    if (!open) return;
    setScope('date');
    setClosed(initial.closed);
    setFrom(minutesToHHMM(initial.fromMinutes));
    setTo(minutesToHHMM(initial.toMinutes));
    setPendingImpact(null);
  }, [open, initial.fromMinutes, initial.toMinutes, initial.closed]);

  const { upsertOpeningHoursExceptionAsync, isUpserting } =
    useUpsertOpeningHoursException({ onSuccess: () => onOpenChange(false) });
  const { updateStandingOpeningHoursAsync, isUpdating } =
    useUpdateStandingOpeningHours({ onSuccess: () => onOpenChange(false) });

  const handleSave = async (confirmedImpactToken?: string) => {
    const fromMinutes = closed ? null : hhmmToMinutes(from);
    const toMinutes = closed ? null : hhmmToMinutes(to);
    if (
      !closed &&
      fromMinutes !== null &&
      toMinutes !== null &&
      toMinutes <= fromMinutes
    ) {
      return; // form-level validation
    }

    if (scope === 'date') {
      try {
        await upsertOpeningHoursExceptionAsync({
          locationId,
          date: format(date, 'yyyy-MM-dd'),
          closed,
          fromMinutes,
          toMinutes,
          confirmedImpactToken,
        });
      } catch (error) {
        const confirmation = getOpeningHoursConfirmation(error);
        if (confirmation) {
          if (confirmedImpactToken)
            toast.info(
              'The schedule changed. Review the updated impact and confirm again.'
            );
          setPendingImpact(confirmation);
        }
        // The update hook already shows the server error.
      }
      return;
    }

    // scope === 'standing': edit the day-of-week's row in the standing
    // schedule, falling back to org default if the location had no standing
    // schedule yet.
    const dow = String(date.getDay());
    const base = standing ?? organizationDefault ?? {};
    const next: LocationOpeningHours = { ...base };
    if (closed) {
      delete next[dow];
    } else if (fromMinutes !== null && toMinutes !== null) {
      next[dow] = { from: fromMinutes, to: toMinutes };
    }
    const nextHours = Object.keys(next).length === 0 ? null : next;
    try {
      await updateStandingOpeningHoursAsync({
        locationId,
        openingHours: nextHours,
        confirmedImpactToken,
      });
    } catch (error) {
      const confirmation = getOpeningHoursConfirmation(error);
      if (confirmation) {
        if (confirmedImpactToken)
          toast.info(
            'The schedule changed. Review the updated impact and confirm again.'
          );
        setPendingImpact(confirmation);
      }
      // The update hook already shows the server error.
    }
  };

  const isSaving = isUpserting || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {pendingImpact
              ? 'Confirm opening-hours change'
              : 'Edit opening hours'}
          </DialogTitle>
          {pendingImpact ? (
            <OpeningHoursImpactDescription {...pendingImpact} />
          ) : (
            <DialogDescription>
              {format(date, 'EEEE, MMMM d, yyyy')}
            </DialogDescription>
          )}
        </DialogHeader>

        {!pendingImpact && (
          <FieldGroup className="gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="oh-closed"
                checked={closed}
                onCheckedChange={(v) => setClosed(v === true)}
              />
              <Label htmlFor="oh-closed" className="cursor-pointer">
                Closed all day
              </Label>
            </div>

            {!closed && (
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="oh-from">Opens</FieldLabel>
                  <Input
                    id="oh-from"
                    type="time"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="oh-to">Closes</FieldLabel>
                  <Input
                    id="oh-to"
                    type="time"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </Field>
              </div>
            )}

            <FieldSet>
              <FieldLegend variant="label">Apply to</FieldLegend>
              <RadioGroup
                value={scope}
                onValueChange={(v) => setScope(v as EditScope)}
                className="gap-4"
              >
                <Field orientation="horizontal">
                  <RadioGroupItem
                    value="date"
                    id="scope-date"
                    aria-describedby="scope-date-description"
                  />
                  <FieldContent>
                    <FieldLabel htmlFor="scope-date" className="cursor-pointer">
                      Just this day
                    </FieldLabel>
                    <FieldDescription id="scope-date-description">
                      Only changes {format(date, 'EEEE, MMM d')}.
                    </FieldDescription>
                  </FieldContent>
                </Field>
                <Field orientation="horizontal">
                  <RadioGroupItem
                    value="standing"
                    id="scope-standing"
                    aria-describedby="scope-standing-description"
                  />
                  <FieldContent>
                    <FieldLabel
                      htmlFor="scope-standing"
                      className="cursor-pointer"
                    >
                      Every {format(date, 'EEEE')} from now on
                    </FieldLabel>
                    <FieldDescription id="scope-standing-description">
                      Updates the standing weekly schedule.
                    </FieldDescription>
                  </FieldContent>
                </Field>
              </RadioGroup>
            </FieldSet>
          </FieldGroup>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() =>
              pendingImpact ? setPendingImpact(null) : onOpenChange(false)
            }
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              pendingImpact
                ? void handleSave(pendingImpact.token)
                : void handleSave()
            }
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : pendingImpact ? 'Confirm changes' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
